import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Empty, Space, Table, Tooltip, Typography } from 'antd';
import type { TableColumnsType } from 'antd';
import { Link } from 'react-router';
import type { LinhaDoFunil } from '@titularis/api-client';
import { api } from '../../api';
import { emReais } from '../../dominio';
import { calcularConversao, formatarConversao, rotuloDoCanal, somarTotaisDoFunil } from '../origem';

/**
 * Origem: esse anúncio deu retorno?
 *
 * O orçamento depende do dinheiro registrado no nosso banco. Eventos do PostHog
 * perdem compras por bloqueadores de anúncio e por navegadores que não voltam do
 * checkout; usá-los aqui faria uma venda paga desaparecer.
 *
 * **A linha sem origem fica separada e visível.** Os três nulos reúnem tráfego
 * direto e tudo o que chegou antes de 13/09/2026, quando a medição começou. No
 * primeiro mês ela deve ser a maior: escondê-la ou distribuí-la entre os canais
 * medidos mentiria sobre quanto esses canais trouxeram.
 *
 * **Os quatro cartões de total saíram em 18/09/2026**, com a mudança de lugar.
 * Eles diziam contas, inscrições, compras e receita - exatamente o que o funil
 * no alto desta mesma tela passou a dizer, e de forma mais completa, porque lá
 * eles vêm com as etapas do meio. Dois blocos com os mesmos quatro números a
 * meia tela de distância obrigam quem lê a conferir se são de fato os mesmos.
 *
 * A tabela não pagina, para a comparação entre canais incluir todas as linhas.
 * A ordem inicial é a da API: receita, contas e inscrições. Só o clique numa
 * coluna numérica muda a prioridade.
 */
const colunas: TableColumnsType<LinhaDoFunil> = [
  {
    title: 'Canal',
    key: 'canal',
    ellipsis: true,
    render: (_: unknown, linha: LinhaDoFunil) =>
      /*
       * O canal leva à lista recortada por ele. É a ponte que faltava entre "o
       * Instagram trouxe 9 contas" e "quem são essas nove" - a pergunta que
       * vinha logo depois e que, até aqui, se respondia à mão.
       */
      linha.source ? (
        <Link to={`/pessoas?canal=${encodeURIComponent(linha.source)}`}>
          {rotuloDoCanal(linha)}
        </Link>
      ) : (
        <Link to="/pessoas?canal=direto">{rotuloDoCanal(linha)}</Link>
      ),
  },
  {
    title: 'Contas',
    dataIndex: 'contas',
    key: 'contas',
    align: 'right',
    width: 110,
    sorter: (primeira, segunda) => primeira.contas - segunda.contas,
  },
  {
    title: 'Inscrições',
    dataIndex: 'inscricoes',
    key: 'inscricoes',
    align: 'right',
    width: 130,
    sorter: (primeira, segunda) => primeira.inscricoes - segunda.inscricoes,
  },
  {
    title: 'Compras',
    dataIndex: 'compras',
    key: 'compras',
    align: 'right',
    width: 120,
    sorter: (primeira, segunda) => primeira.compras - segunda.compras,
  },
  {
    title: 'Receita',
    dataIndex: 'centavos',
    key: 'centavos',
    align: 'right',
    width: 170,
    render: (centavos: number) => emReais(centavos),
    sorter: (primeira, segunda) => primeira.centavos - segunda.centavos,
  },
  {
    title: (
      <Tooltip title="Compras / contas. A conta usa o primeiro canal; a compra, o canal da venda. A taxa pode superar 100%. Sem contas, não há taxa.">
        Conversão
      </Tooltip>
    ),
    key: 'conversao',
    align: 'right',
    width: 140,
    render: (_: unknown, linha: LinhaDoFunil) => formatarConversao(linha),
    // Sem taxa vem antes de zero na ordem crescente: ausência não é 0%.
    sorter: (primeira, segunda) =>
      (calcularConversao(primeira) ?? -1) - (calcularConversao(segunda) ?? -1),
  },
];

export function OrigemDasPessoas() {
  const funil = useQuery({
    queryKey: ['funil-por-origem'],
    queryFn: () => api.funilPorOrigem(),
  });

  const linhas = funil.data ?? [];
  const totais = somarTotaisDoFunil(linhas);

  if (funil.error) {
    return (
      <Alert
        type="error"
        showIcon
        title={funil.error.message}
        action={
          <Button loading={funil.isFetching} onClick={() => void funil.refetch()}>
            Tentar novamente
          </Button>
        }
      />
    );
  }

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Typography.Text type="secondary">
        Contas, inscrições ativas e compras pagas por canal. A receita vem dos pagamentos
        registrados no nosso banco, e não do PostHog. Clique num canal para ver quem veio por ele.
      </Typography.Text>

      <Card styles={{ body: { padding: 0 } }}>
        <Table<LinhaDoFunil>
          // O rótulo pode conter separadores; a tupla preserva canais distintos.
          rowKey={(linha) => JSON.stringify([linha.source, linha.medium, linha.campaign])}
          dataSource={linhas}
          columns={colunas}
          loading={funil.isFetching}
          locale={{
            emptyText: <Empty description="Nenhuma conta, inscrição ou compra registrada ainda." />,
          }}
          pagination={false}
          scroll={{ x: 1050 }}
          /*
           * O rodapé soma as colunas, e aqui ele **não** repete o funil de cima:
           * aquele conta pessoas por etapa, este soma canais. São os mesmos
           * fatos por eixos diferentes, e a soma da coluna é o que prova que
           * nenhuma linha ficou de fora - inclusive a sem medição.
           */
          summary={() =>
            linhas.length > 0 ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Typography.Text strong>Todos os canais</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Typography.Text strong>{totais.contas}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <Typography.Text strong>{totais.inscricoes}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <Typography.Text strong>{totais.compras}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <Typography.Text strong>{emReais(totais.centavos)}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <Typography.Text strong>{formatarConversao(totais)}</Typography.Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            ) : null
          }
        />
      </Card>
    </Space>
  );
}
